import { authUrl } from '@algoan/dashboard/env';
import { serverLogger } from '@algoan/server-logger';
import { parseJwtPayload } from './token-utils';
import NextAuth, { AuthOptions as NextAuthOptions } from 'next-auth';
import { JWT as NextAuthJWT } from 'next-auth/jwt';
import KeycloakProvider from 'next-auth/providers/keycloak';
import { AccessTokenPayload, AuthInformation, AuthOptions } from './model';

type JWT = NextAuthJWT & AuthInformation;

const MILLISECONDS_IN_SECOND = 1000;

const refreshAccessToken = async ({ token, clientId }: { token: JWT; clientId: string }): Promise<JWT> => {
  serverLogger.info('Refreshing access token', { email: token.email, token, clientId });
  const resp = await fetch(`${authUrl}/protocol/openid-connect/token`, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      /* eslint-disable @typescript-eslint/naming-convention */
      client_id: clientId,
      client_secret: '',
      grant_type: 'refresh_token',
      refresh_token: token.refreshToken,
      /* eslint-enable @typescript-eslint/naming-convention */
    }),
    method: 'POST',
  });
  const refreshToken = await resp.json();
  if (!resp.ok) {
    serverLogger.error(
      {
        email: token.email,
        status: resp.status,
        refreshToken: token.refreshToken,
      },
      'Error refreshing access token',
    );
    throw refreshToken;
  }

  return {
    ...token,
    accessToken: refreshToken.access_token,
    idToken: refreshToken.id_token,
    refreshToken: refreshToken.refresh_token,
    accessTokenExpiresAt: Date.now() + (refreshToken.expires_in ?? 0) * MILLISECONDS_IN_SECOND,
  };
};

export const getAuthOptions = ({ clientId, authSecret, pages }: AuthOptions): NextAuthOptions => ({
  secret: authSecret,
  providers: [
    KeycloakProvider({
      clientId,
      clientSecret: '', // not needed, just added to avoid TypeScript error
      issuer: authUrl,
    }),
  ],
  callbacks: {
    async jwt({ token, account, user }) {
      const enrichedToken = token as JWT;
      serverLogger.info({ email: enrichedToken.email, token, account, user }, 'JWT callback called');
      const isInitialSignIn = account && user;
      if (isInitialSignIn) {
        serverLogger.info({ email: enrichedToken.email, token, account, user }, 'Initial sign-in detected');
        return {
          ...enrichedToken,
          accessToken: account.access_token,
          refreshToken: account.refresh_token,
          idToken: account.id_token,
          accessTokenExpiresAt: (account.expires_at ?? 0) * MILLISECONDS_IN_SECOND,
        };
      }

      // Return previous token if the access token has not expired yet
      const isTokenExpired = enrichedToken.accessTokenExpiresAt <= Date.now();
      const parsedToken = parseJwtPayload<AccessTokenPayload>(enrichedToken.accessToken);
      const isLinkedToAnOrganisation = parsedToken.organizationIds !== undefined;

      if (
        isTokenExpired ||
        !isLinkedToAnOrganisation // If the token is not linked to an organization, we want to refresh it (case of first connection after registration)
      ) {
        if (isTokenExpired) {
          serverLogger.info(
            {
              email: enrichedToken.email,
              accessTokenExpiresAt: enrichedToken.accessTokenExpiresAt,
              currentTime: Date.now(),
              token,
              account,
              user,
            },
            'Access token has expired, refreshing...',
          );
        } else {
          serverLogger.info(
            {
              email: enrichedToken.email,
              token,
              account,
              user,
            },
            'Access token is not linked to an organization, refreshing...',
          );
        }
        return refreshAccessToken({ token: enrichedToken, clientId });
      }

      return token;
    },
    async session({ session, token }) {
      serverLogger.info({ email: session.user?.email, session, token }, 'Session callback called');
      const enrichedToken = token as JWT;
      return {
        ...session,
        data: {
          accessToken: enrichedToken.accessToken,
          refreshToken: enrichedToken.refreshToken,
          idToken: enrichedToken.idToken,
        },
      };
    },
  },
  events: {
    async signOut({ token }: { token: NextAuthJWT }) {
      serverLogger.info('User sign out', { email: token.email, token });
      const logOutUrl = new URL(`${authUrl}/protocol/openid-connect/logout`);
      logOutUrl.searchParams.set('id_token_hint', (token as JWT).idToken);
      await fetch(logOutUrl);
    },
  },
  pages,
});

export const authHandler = (params: AuthOptions): unknown => {
  const authOptions = getAuthOptions(params);
  return NextAuth(authOptions);
};
